import React, { useEffect, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { useOutletContext } from 'react-router-dom';

import styles from './DataQuality.styles';
import CenteredLoader from '../../../../components/loaders/PageLoader';

import DataQualityHeader from '../../components/DataQuality/DataQualityHeader';
import DataQualityBarChart from '../../components/DataQuality/DataQualityBarChart';
import DataQualityPieChart from '../../components/DataQuality/DataQualityPieChart';
import DataQualityTable from '../../components/DataQuality/DataQualityTable';

import { useDataQuality } from '../../hooks/useDataQuality';
import { useFilterDirectory } from '../../hooks/useFilterDirectory';

import useDateRange from '../../hooks/useDateRange';
import useFilterGraph from '../../hooks/useFilterGraph';
import useFiltersState from '../../hooks/useFiltersState';
import { exportDataQualityCsv } from '../../utils/exporters';

import { makeDefaultRange14 } from '../../utils/dateRanges';
import { buildQualitySeries } from '../../utils/dataQualityBar.helpers';
import { makeTableRows } from '../../utils/dataQualityTable.helpers';

export default function DataQuality() {
  const { isSidebarExpanded } = useOutletContext();

  const { dateRange, draftRange, setDraftRange, applyDateRange } = useDateRange();

  const { operators, units, meters, loading: dirLoading, error: dirError } = useFilterDirectory();
  const graph = useFilterGraph({ operators, units, meters });
  const { filters, setFilters, state } = useFiltersState(graph);

  useEffect(() => {
    const hasDraft = Boolean(draftRange?.start) && Boolean(draftRange?.end);

    if (hasDraft) {
      if (draftRange.start !== dateRange.start || draftRange.end !== dateRange.end) {
        applyDateRange(draftRange);
      }
      return;
    }

    const def = makeDefaultRange14();
    if (dateRange.start !== def.start || dateRange.end !== def.end) {
      applyDateRange(null);
    }
  }, [applyDateRange, dateRange.start, dateRange.end, draftRange]);

  const { data, loading } = useDataQuality({
    operator: filters.operator[0] ?? null,
    unit: filters.unit[0] ?? null,
    meter: filters.meter[0] ?? null,
    fromDate: dateRange.start,
    toDate: dateRange.end,
  });

  const continuousSeries = useMemo(
    () => buildQualitySeries(data, dateRange),
    [data, dateRange]
  );

  const rows = useMemo(
    () => makeTableRows(continuousSeries),
    [continuousSeries]
  );

  const handleExport = () => exportDataQualityCsv(rows);

  if (dirError) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">{String(dirError)}</Typography>
      </Box>
    );
  }

  if (dirLoading || loading) {
    return (
      <Box
        sx={{
          ...styles.pageContent,
          marginLeft: isSidebarExpanded ? '240px' : '95px',
          width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
          transition: 'all 0.3s ease',
        }}
      >
        <CenteredLoader />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...styles.pageContent,
        marginLeft: isSidebarExpanded ? '240px' : '95px',
        width: isSidebarExpanded ? 'calc(100vw - 240px)' : 'calc(100vw - 95px)',
        transition: 'all 0.3s ease',
      }}
    >
      <DataQualityHeader
        title="Data Quality Summary"
        filters={filters}
        setFilters={setFilters}
        state={state}
        draftRange={draftRange}
        setDraftRange={setDraftRange}
        applyDateRange={applyDateRange}
        onExport={handleExport}
      />

      <Box sx={styles.innerCardContainer}>
        <DataQualityBarChart data={data} dateRange={dateRange} />
        <DataQualityPieChart data={data} dateRange={dateRange} />
        <DataQualityTable rows={rows} />
      </Box>
    </Box>
  );
}